import * as Y from "yjs";

const SHARED_ASSETS_KEY = "shared-image-assets";

export interface SharedImageAsset {
  sourcePath: string;
  referencePath: string;
  mimeType: string;
  bytes: Uint8Array;
  updatedAt: string;
}

export interface ReferencedImageOccurrence {
  referencePath: string;
  offset: number;
}

export function getSharedImageAssets(doc: Y.Doc): SharedImageAsset[] {
  const assets = getSharedImageAssetCollection(doc);
  return Array.from(assets.entries())
    .map(([, value]) => serializeSharedImageAsset(value))
    .sort((left, right) => left.sourcePath.localeCompare(right.sourcePath));
}

export function observeSharedImageAssets(
  doc: Y.Doc,
  listener: (assets: SharedImageAsset[]) => void
): () => void {
  const assets = getSharedImageAssetCollection(doc);
  const handler = (): void => {
    listener(getSharedImageAssets(doc));
  };

  assets.observeDeep(handler);
  handler();

  return () => {
    assets.unobserveDeep(handler);
  };
}

export function upsertSharedImageAsset(
  doc: Y.Doc,
  asset: SharedImageAsset,
  origin?: unknown
): boolean {
  const assets = getSharedImageAssetCollection(doc);
  const existing = assets.get(asset.sourcePath);
  if (existing) {
    const serializedExisting = serializeSharedImageAsset(existing);
    if (
      serializedExisting.referencePath === asset.referencePath &&
      serializedExisting.mimeType === asset.mimeType &&
      uint8ArraysEqual(serializedExisting.bytes, asset.bytes)
    ) {
      return false;
    }
  }

  doc.transact(() => {
    const map = new Y.Map<unknown>();
    map.set("sourcePath", asset.sourcePath);
    map.set("referencePath", asset.referencePath);
    map.set("mimeType", asset.mimeType);
    map.set("bytes", asset.bytes);
    map.set("updatedAt", asset.updatedAt);
    assets.set(asset.sourcePath, map);
  }, origin);

  return true;
}

export function extractReferencedImagePaths(markdown: string): string[] {
  const paths: string[] = [];
  const seen = new Set<string>();

  const obsidianEmbedRegex = /!\[\[([^[\]|]+)(?:\|[^\]]*)?\]\]/g;
  const markdownImageRegex = /!\[[^\]]*]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;

  for (const regex of [obsidianEmbedRegex, markdownImageRegex]) {
    for (const match of markdown.matchAll(regex)) {
      const rawPath = match[1]?.trim();
      if (!rawPath || isExternalAssetPath(rawPath) || seen.has(rawPath)) {
        continue;
      }

      seen.add(rawPath);
      paths.push(rawPath);
    }
  }

  return paths;
}

export function extractReferencedImageOccurrences(markdown: string): ReferencedImageOccurrence[] {
  const occurrences: ReferencedImageOccurrence[] = [];
  const obsidianEmbedRegex = /!\[\[([^[\]|]+)(?:\|[^\]]*)?\]\]/g;
  const markdownImageRegex = /!\[[^\]]*]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;

  for (const regex of [obsidianEmbedRegex, markdownImageRegex]) {
    for (const match of markdown.matchAll(regex)) {
      const rawPath = match[1]?.trim();
      if (!rawPath || isExternalAssetPath(rawPath)) {
        continue;
      }

      occurrences.push({
        referencePath: rawPath,
        offset: match.index ?? 0
      });
    }
  }

  return occurrences.sort((left, right) => left.offset - right.offset);
}

export function getMimeTypeForImagePath(path: string): string {
  const extension = path.split(".").pop()?.toLowerCase() ?? "";
  switch (extension) {
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "svg":
      return "image/svg+xml";
    case "avif":
      return "image/avif";
    case "bmp":
      return "image/bmp";
    default:
      return "application/octet-stream";
  }
}

function getSharedImageAssetCollection(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap<Y.Map<unknown>>(SHARED_ASSETS_KEY);
}

function serializeSharedImageAsset(asset: Y.Map<unknown>): SharedImageAsset {
  return {
    sourcePath: String(asset.get("sourcePath") ?? ""),
    referencePath: String(asset.get("referencePath") ?? ""),
    mimeType: String(asset.get("mimeType") ?? "application/octet-stream"),
    bytes: toUint8Array(asset.get("bytes")),
    updatedAt: String(asset.get("updatedAt") ?? new Date(0).toISOString())
  };
}

function toUint8Array(value: unknown): Uint8Array {
  if (value instanceof Uint8Array) {
    return value;
  }

  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }

  if (Array.isArray(value)) {
    return Uint8Array.from(value);
  }

  return new Uint8Array();
}

function uint8ArraysEqual(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) {
    return false;
  }

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }

  return true;
}

function isExternalAssetPath(path: string): boolean {
  return /^(https?:)?\/\//i.test(path) || path.startsWith("data:");
}
